import { bottomNavigationActionClasses } from "@mui/material";
import { arSA, enUS } from "@mui/material/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { CaretDownIcon } from "@phosphor-icons/react";
import { type ComponentProps, type ReactNode, useMemo } from "react";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

export const SelectIcon = (props: ComponentProps<typeof CaretDownIcon>) => (
  <CaretDownIcon {...props} size={20} />
);

export const MuiThemeProvider = ({ children }: { children: ReactNode }) => {
  const { dir, locale } = useLocaleInfo();

  const theme = useMemo(
    () =>
      createTheme(
        {
          direction: dir,
          // it's not possible to use CSS variables here:
          // https://github.com/mui/material-ui/issues/12827
          palette: {
            primary: {
              main: "#003E2B", // same as --color-surface-accent-stronger
            },
            error: {
              main: "#DE370C", // same as --color-semantic-error-strong
            },
            success: {
              main: "#2e8285", // same as --color-semantic-success-strong
            },
            whatsapp: {
              main: "#40C351", // same as --color-whatsapp-green
            },
          },
          typography: {
            fontFamily: `var(--font-sans)`,
          },
          components: {
            MuiBadge: {
              styleOverrides: {
                badge: {
                  height: "14px",
                  minWidth: "14px",
                  fontWeight: "bold",
                  fontSize: "10px",
                  lineHeight: "12px",
                  padding: 0,
                },
              },
            },
            MuiLink: {
              styleOverrides: {
                root: {
                  textDecoration: "none",
                  fontWeight: "bold",
                  fontSize: 14,
                },
              },
            },
            MuiFormControlLabel: {
              defaultProps: {
                slotProps: {
                  typography: {
                    fontSize: 14,
                    color: "var(--color-text-label)",
                    fontWeight: "var(--font-weight-medium)",
                  },
                },
              },
              styleOverrides: {
                root: {
                  margin: 0,
                },
              },
            },
            MuiRadio: {
              styleOverrides: {
                root: {
                  padding: 8,

                  "& .MuiSvgIcon-root": {
                    fontSize: 24,
                  },
                  "& .MuiSvgIcon-root:nth-of-type(2)": {
                    fontSize: 18,
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) scale(0)",
                  },
                  "&.Mui-checked": {
                    color: "var(--color-surface-accent-stronger)",

                    "& .MuiSvgIcon-root:nth-of-type(2)": {
                      transform: "translate(-50%, -50%) scale(1)",
                    },
                  },
                },
              },
            },
            MuiAvatar: {
              styleOverrides: {
                root: {
                  // arabic font is not centered properly
                  // only latin characters are expected here
                  fontFamily: "var(--font-sans-input)",
                },
              },
            },
            MuiBottomNavigationAction: {
              defaultProps: {
                disableRipple: true,
              },
              styleOverrides: {
                root: {
                  padding: "0 calc(var(--spacing)*2)",
                  color: "var(--color-text-tertiary)",
                  height: "58px",

                  svg: {
                    width: "calc(var(--spacing)*6)",
                    height: "calc(var(--spacing)*6)",
                  },

                  [`&.${bottomNavigationActionClasses.selected}`]: {
                    color: "var(--color-surface-accent-stronger)",
                    fontSize: "inherit",
                  },
                },
                label: {
                  marginTop: "calc(var(--spacing)*0.5)",
                  fontWeight: "var(--font-weight-medium)",
                  fontSize: "var(--text-xs)",
                  lineHeight: "var(--text-xs--line-height)",
                  letterSpacing: "var(--tracking-wide)",

                  [`&.${bottomNavigationActionClasses.selected}`]: {
                    fontSize: "var(--text-xs)", // to prevent text enlargment on selection
                    fontWeight: "var(--font-weight-bold)",
                    letterSpacing: "var(--tracking-normal)",
                  },
                },
              },
            },
            MuiSkeleton: {
              defaultProps: {
                animation: "wave",
                variant: "rounded",
              },
              styleOverrides: {
                root: {
                  backgroundColor: "var(--color-stroke-neutral-softer)",
                  variants: [
                    {
                      props: { variant: "rounded" },
                      style: {
                        borderRadius: "var(--radius-lg)",
                      },
                    },
                  ],
                },
              },
            },
            MuiChip: {
              styleOverrides: {
                root: {
                  variants: [
                    {
                      props: { color: "primary" },
                      style: {
                        backgroundColor: "var(--color-surface-accent-stronger)",
                        color: "var(--color-text-inverted)",
                      },
                    },
                    {
                      props: { color: "success" },
                      style: {
                        backgroundColor: "var(--color-core-rahal-100)",
                        color: "var(--color-core-rahal-600)",
                      },
                    },
                    {
                      props: { color: "warning" },
                      style: {
                        backgroundColor: "rgba(251, 230, 208, 0.5)",
                        color: "var(--color-amber-600)",
                      },
                    },
                    {
                      props: { color: "error" },
                      style: {
                        backgroundColor: "rgba(254, 243, 242, 1)",
                        color: "var(--color-semantic-error-strong)",
                      },
                    },
                  ],
                },
              },
            },
            MuiCheckbox: {
              styleOverrides: {
                root: {
                  color: "var(--color-stroke-neutral-strong)",
                  "&.Mui-checked": {
                    backgroundColor: "#fff",
                    color: "var(--color-text-interactive)",
                  },
                },
              },
            },
            MuiSlider: {
              styleOverrides: {
                root: {
                  height: 6,
                },
                rail: {
                  backgroundColor: "var(--color-stroke-neutral-soft)",
                  opacity: 1,
                },
                thumb: {
                  width: 16,
                  height: 24,
                  backgroundColor: "var(--color-surface-accent-medium)",
                  borderRadius: "4px",
                  border: "7px solid var(--color-surface-accent-stronger)",
                },
              },
            },
            MuiSnackbar: {
              styleOverrides: {
                root: {
                  bottom:
                    "calc(var(--main-bottom-padding) + max(var(--drawer-height, 0px), var(--footer-height, 0px))) !important",
                },
              },
            },
            MuiSnackbarContent: {
              styleOverrides: {
                root: {
                  minWidth: 0,
                  flexGrow: 0,
                  padding: "0 calc(var(--spacing)*3)",
                  maxWidth: "var(--container-2xs)",
                  borderRadius: "var(--radius-lg)",
                  backgroundColor: "var(--color-zinc-800)",
                  boxShadow: "var(--shadow-md)",
                },
              },
            },
            MuiDialog: {
              styleOverrides: {
                paper: {
                  borderRadius: "var(--radius-2xl)",
                  padding: "calc(var(--spacing)*2)",
                },
              },
            },
            MuiAutocomplete: {
              defaultProps: {
                popupIcon: <SelectIcon />,
              },
            },
            MuiButton: {
              styleOverrides: {
                root: {
                  variants: [
                    {
                      props: { color: "primary", variant: "contained" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "var(--color-surface-pressed-accent-strong)",
                        },
                        "&:disabled, &:disabled:focus-visible": {
                          backgroundColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-inverted)",
                        },
                      },
                    },
                    {
                      props: { color: "primary", variant: "outlined" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-surface-accent-stronger) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "primary", variant: "text" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-surface-accent-stronger) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "secondary", variant: "text" },
                      style: {
                        "&:hover:not([disabled]), &:focus-visible:not([disabled])":
                          {
                            backgroundColor:
                              "rgb(from var(--color-surface-accent-stronger) r g b / 10%)",
                          },
                        "&:not([disabled])": {
                          color: "var(--color-text-secondary)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "contained" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "var(--color-semantic-error-pressed-strong)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "outlined" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-error-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "error", variant: "text" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-error-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "contained" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "var(--color-semantic-success-pressed-strong)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "outlined" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-success-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "success", variant: "text" },
                      style: {
                        "&:focus-visible:not([disabled])": {
                          backgroundColor:
                            "rgb(from var(--color-semantic-success-strong) r g b / 10%)",
                        },
                      },
                    },
                    {
                      props: { color: "whatsapp", variant: "contained" },
                      style: {
                        color: "var(--color-text-inverted)",
                      },
                    },
                    {
                      props: { size: "small" },
                      style: {
                        fontSize: "14px",
                        lineHeight: "20px",
                        paddingTop: "calc(var(--spacing)*2)",
                        paddingBottom: "calc(var(--spacing)*2)",
                        paddingLeft: "calc(var(--spacing)*4)",
                        paddingRight: "calc(var(--spacing)*4)",
                      },
                    },
                    {
                      props: { size: "medium" },
                      style: {
                        fontSize: "16px",
                        lineHeight: "22px",
                        paddingTop: "calc(var(--spacing)*3)",
                        paddingBottom: "calc(var(--spacing)*3)",
                        paddingLeft: "calc(var(--spacing)*6)",
                        paddingRight: "calc(var(--spacing)*6)",
                      },
                    },
                    {
                      props: { size: "large" },
                      style: {
                        fontSize: "18px",
                        lineHeight: "26px",
                        fontWeight: "var(--font-weight-bold)",
                        paddingTop: "calc(var(--spacing)*3.5)",
                        paddingBottom: "calc(var(--spacing)*3.5)",
                        paddingLeft: "calc(var(--spacing)*8)",
                        paddingRight: "calc(var(--spacing)*8)",
                      },
                    },
                    {
                      props: { variant: "contained" },
                      style: {
                        "&:disabled, &:disabled:focus-visible": {
                          backgroundColor:
                            "var(--color-surface-disabled-accent-strong)",
                          borderColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-inverted)",
                        },
                      },
                    },
                    {
                      props: { variant: "outlined" },
                      style: {
                        "&:disabled, &:disabled:focus-visible": {
                          backgroundColor: "transparent",
                          borderColor:
                            "var(--color-surface-disabled-accent-strong)",
                          color: "var(--color-text-disabled-interactive)",
                        },
                      },
                    },
                    {
                      props: { variant: "text" },
                      style: {
                        fontWeight: "var(--font-weight-semibold)",
                        "&:disabled, &:disabled:focus-visible": {
                          backgroundColor: "transparent",
                          color: "var(--color-text-disabled-interactive)",
                        },
                      },
                    },
                  ],
                  textTransform: "none",
                  fontWeight: "var(--font-weight-bold)",
                  borderRadius: "999px",
                  borderColor: "var(--color-stroke-interactive-strong)",
                },
              },
            },
            MuiDrawer: {
              styleOverrides: {
                paper: {
                  margin: "auto",
                  maxWidth: "var(--container-xl)",
                  borderRadius: "var(--radius-2xl) var(--radius-2xl) 0 0",
                  padding:
                    "calc(var(--spacing)*6) calc(var(--spacing)*4) var(--drawer-bottom-padding)",
                  overscrollBehavior: "none",
                },
              },
            },
            MuiSelect: {
              defaultProps: {
                variant: "outlined",
                IconComponent: SelectIcon,
              },
              styleOverrides: {
                root: {
                  // we need separate font for form inputs because of issues with noto sans arabic height
                  "& .MuiSelect-select": {
                    fontFamily: "var(--font-sans-input)",
                  },
                  ".MuiSelect-icon": {
                    insetInlineEnd: "calc(var(--spacing)*3)",
                    color: "var(--color-text-placeholder)",
                    transition: "transform 0.1s ease-in-out",
                    top: "unset",
                  },
                  variants: [
                    {
                      props: { variant: "outlined" },
                      style: {
                        borderRadius: "16px",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E7E9EE",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-surface-accent-stronger)",
                          borderWidth: "1px !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "var(--color-surface-accent-stronger)",
                          borderWidth: "1px !important",
                        },
                      },
                    },
                  ],
                },
              },
            },
            MuiAlert: {
              styleOverrides: {
                root: {
                  // padding: "calc(var(--spacing)*3)",
                  padding: "0 calc(var(--spacing)*3)",
                  border: "1px solid var(--color-stroke-neutral-soft)",
                  borderRadius: "var(--radius-2xl)",

                  ".MuiAlert-icon": {
                    padding: "calc(var(--spacing)*3) 0",
                    marginInlineEnd: "calc(var(--spacing)*2)",
                  },

                  ".MuiAlert-message": {
                    padding: "calc(var(--spacing)*3) 0",
                    fontSize: "14px",
                    lineHeight: "18px",
                  },

                  variants: [
                    {
                      props: { severity: "success" },
                      style: {
                        backgroundColor: "var(--color-semantic-success-soft)",
                      },
                    },
                    {
                      props: { severity: "error" },
                      style: {
                        backgroundColor:
                          "var(--color-semantic-error-pressed-soft)",
                      },
                    },
                    {
                      props: { severity: "info" },
                      style: {
                        backgroundColor: "var(--color-semantic-success-soft)",

                        ".MuiAlert-icon": {
                          color: "var(--color-semantic-success-strong)",
                        },
                        ".MuiAlert-message": {
                          color: "var(--color-text-primary)",
                        },
                      },
                    },
                  ],
                },
              },
            },
            MuiInputLabel: {
              styleOverrides: {
                root: ({ ownerState }) => {
                  const formControl = ownerState?.formControl as
                    | {
                        filled?: boolean;
                        focused?: boolean;
                        adornedStart?: boolean;
                      }
                    | undefined;

                  return {
                    color: "var(--color-text-placeholder)",
                    fontSize: "16px",
                    lineHeight: "22px",
                    letterSpacing: "0.15px",
                    variants: [
                      {
                        props: ({ variant, hiddenLabel }) =>
                          variant === "outlined" && !hiddenLabel,
                        style: () => {
                          const isLabelShrunk =
                            formControl?.filled ||
                            formControl?.focused ||
                            ownerState.shrink;

                          return {
                            transform: "translate(12px, 16px) scale(1)",
                            ...(isLabelShrunk && {
                              transform: "translate(12px, 7px) scale(0.875)",
                            }),

                            ...(formControl?.adornedStart && {
                              // 12px(padding) + 24px(icon) + 6px(gap) + 2px(input's native offset) = 44px
                              transform: "translate(44px, 16px) scale(1)",
                              pointerEvents: "none",

                              ...(isLabelShrunk && {
                                transform: "translate(44px, 7px) scale(0.875)",
                              }),
                            }),
                          };
                        },
                      },
                    ],
                  };
                },
              },
            },
            MuiPopover: {
              styleOverrides: {
                paper: {
                  borderRadius: "var(--radius-lg)",
                },
              },
            },
            MuiFormHelperText: {
              styleOverrides: {
                root: {
                  marginInline: 0,
                },
              },
            },
            MuiTextField: {
              defaultProps: {
                variant: "outlined",
                slotProps: {
                  select: {
                    IconComponent: SelectIcon,
                  },
                },
              },
              styleOverrides: {
                root: {
                  "& .MuiInputBase-root.Mui-disabled": {
                    backgroundColor: "var(--color-interactive-soft-disabled)",
                  },

                  // we need separate font for form inputs because of issues with noto sans arabic height
                  "& .MuiInputBase-input": {
                    fontFamily: "var(--font-sans-input)",
                    fontWeight: 500,
                    color: "var(--color-text-label)",
                  },

                  "& .MuiSelect-select": {
                    borderRadius: "16px !important",
                  },

                  "& .MuiSelect-icon": {
                    insetInlineEnd: "calc(var(--spacing)*3)",
                    color: "var(--color-text-placeholder)",
                    transition: "transform 0.1s ease-in-out",
                    top: "unset",
                  },

                  "& .Mui-focused": {
                    "& .MuiInputBase-input": {
                      borderColor: "var(--color-surface-accent-stronger)",
                    },
                  },

                  "& .MuiInputAdornment-root > svg": {
                    width: "24px",
                    height: "24px",
                    color: "var(--color-text-placeholder)",
                  },

                  "& .Mui-disabled .MuiInputAdornment-root > svg": {
                    color: "var(--color-interactive-strong-disabled)",
                  },

                  variants: [
                    {
                      props: { variant: "outlined" },
                      style: {
                        "& .MuiInputBase-root": {
                          borderRadius: "16px",
                          backgroundColor: "var(--color-white)",

                          "&.MuiAutocomplete-inputRoot": {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },

                          "& .MuiInputBase-input": {
                            paddingInlineStart: "calc(var(--spacing)*3)",
                            paddingInlineEnd: "calc(var(--spacing)*3)",
                          },

                          "&:has(.MuiInputAdornment-positionStart)": {
                            paddingInlineStart: "calc(var(--spacing)*3)",

                            "& .MuiInputBase-input": {
                              paddingInlineStart: 0,
                            },
                          },
                          "&:has(.MuiInputAdornment-positionEnd)": {
                            paddingInlineEnd: "calc(var(--spacing)*3)",

                            "& .MuiInputBase-input": {
                              paddingInlineEnd: 0,
                            },
                          },
                        },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderWidth: "1px !important",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E7E9EE",
                        },
                      },
                    },
                    {
                      props: ({ variant, hiddenLabel }) =>
                        variant === "outlined" && !hiddenLabel,
                      style: {
                        "& .MuiOutlinedInput-notchedOutline": {
                          "legend > span": {
                            display: "none",
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          marginTop: "27px",
                          marginBottom: "6px",
                          paddingTop: 0,
                          paddingBottom: 0,
                          lineHeight: "137.5%",

                          "&.MuiInputBase-input.MuiAutocomplete-input": {
                            paddingTop: 0,
                            paddingBottom: 0,
                          },
                        },
                      },
                    },
                    {
                      props: { size: "small" },
                      style: {
                        "& .MuiOutlinedInput-input": {
                          marginTop: "10px",
                          marginBottom: "11px",
                        },
                        ".MuiOutlinedInput-notchedOutline": {
                          top: "-1px",
                        },
                      },
                    },
                  ],
                },
              },
            },
            MuiMobileStepper: {
              styleOverrides: {
                root: {
                  width: "100%",
                  paddingInline: 0,
                  backgroundColor: "transparent",
                  "& .MuiLinearProgress-root": {
                    width: "100%",
                    background: "#E7E9EE",
                    borderRadius: "4px",
                  },
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "var(--color-surface-accent-stronger)",
                    borderRadius: "4px",
                  },
                },
              },
            },
          },
        },
        locale === "ar" ? arSA : enUS,
      ),
    [dir, locale],
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};
